<template>
  <div class="mc-datepicker-container" v-click-outside="closePicker">
    <div class="mc-input-trigger" @click="togglePicker">
      <span v-if="modelValue">{{ modelValue }}</span>
      <span v-else class="placeholder">请选择日期</span>
      <div class="calendar-icon"></div>
    </div>

    <transition name="mc-fade">
      <div class="mc-calendar-panel" v-if="show">
        <div class="calendar-header">
          <button class="nav-btn" @click.stop="prevMonth">&lt;</button>
          <div class="current-month">{{ currentYear }}年 {{ currentMonth + 1 }}月</div>
          <button class="nav-btn" @click.stop="nextMonth">&gt;</button>
        </div>
        
        <div class="calendar-weeks">
          <span v-for="w in weeks" :key="w">{{ w }}</span>
        </div>
        
        <div class="calendar-days">
          <div 
            v-for="(day, idx) in days" 
            :key="idx"
            class="day-cell"
            :class="{ 
              'other-month': !day.isCurrentMonth,
              'selected': isSelected(day),
              'disabled': isDisabled(day),
              'today': isToday(day)
            }"
            @click.stop="selectDay(day)"
          >
            {{ day.date.getDate() }}
          </div>
        </div>

        <div class="calendar-footer">
          <button class="mc-mini-btn" @click.stop="selectToday">今天</button>
          <button class="mc-mini-btn" @click.stop="closePicker">确定</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'McDatepicker',
  props: {
    modelValue: String,
    maxDate: String
  },
  emits: ['update:modelValue'],
  directives: {
    'click-outside': {
      beforeMount(el, binding) {
        el.__clickOutsideHandler__ = (e) => {
          if (!(el === e.target || el.contains(e.target))) {
            binding.value(e);
          }
        };
        document.addEventListener('click', el.__clickOutsideHandler__);
      },
      unmounted(el) {
        document.removeEventListener('click', el.__clickOutsideHandler__);
      }
    }
  },
  data() {
    const initialDate = this.modelValue ? new Date(this.modelValue) : new Date();
    return {
      show: false,
      currentYear: initialDate.getFullYear(),
      currentMonth: initialDate.getMonth(),
      weeks: ['日', '一', '二', '三', '四', '五', '六']
    };
  },
  computed: {
    days() {
      const days = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const startDay = new Date(firstDay);
      startDay.setDate(firstDay.getDate() - firstDay.getDay());

      for (let i = 0; i < 42; i++) {
        const d = new Date(startDay);
        d.setDate(startDay.getDate() + i);
        days.push({
          date: d,
          isCurrentMonth: d.getMonth() === this.currentMonth
        });
      }
      return days;
    }
  },
  methods: {
    togglePicker() {
      this.show = !this.show;
    },
    closePicker() {
      this.show = false;
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentYear--;
        this.currentMonth = 11;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentYear++;
        this.currentMonth = 0;
      } else {
        this.currentMonth++;
      }
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    selectDay(day) {
      if (this.isDisabled(day)) return;
      const val = this.formatDate(day.date);
      this.$emit('update:modelValue', val);
      this.show = false;
    },
    selectToday() {
      const today = new Date();
      if (this.maxDate && this.formatDate(today) > this.maxDate) return;
      this.$emit('update:modelValue', this.formatDate(today));
      this.show = false;
    },
    isSelected(day) {
      return this.modelValue === this.formatDate(day.date);
    },
    isDisabled(day) {
      if (!this.maxDate) return false;
      return this.formatDate(day.date) > this.maxDate;
    },
    isToday(day) {
      return this.formatDate(day.date) === this.formatDate(new Date());
    }
  }
};
</script>

<style scoped>
.mc-datepicker-container {
  position: relative;
  width: 100%;
  font-family: 'Microsoft YaHei', sans-serif;
}

.mc-input-trigger {
  background: #000;
  border: 4px solid #7a7a7a;
  padding: 8px 10px;
  color: #9ae054;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  box-shadow: inset 3px 3px 0 rgba(0, 0, 0, 0.4);
}

.placeholder {
  color: #555;
}

.calendar-icon {
  width: 16px;
  height: 16px;
  background: #9ae054;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z'/%3E%3C/svg%3E") no-repeat center;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10z'/%3E%3C/svg%3E") no-repeat center;
}

.mc-calendar-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: #c6c6c6;
  border: 4px solid #000;
  box-shadow: inset -4px -4px 0 #555, inset 4px 4px 0 #fff, 8px 8px 0 rgba(0,0,0,0.3);
  padding: 4px;
  width: 230px;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  color: #000;
  font-weight: bold;
}

.nav-btn {
  background: #999;
  border: 2px solid #000;
  box-shadow: inset -2px -2px 0 #555, inset 2px 2px 0 #ddd;
  padding: 0 6px;
  cursor: pointer;
  font-size: 10px;
  line-height: 1;
}

.nav-btn:hover {
  background: #aaa;
}

.calendar-weeks {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  color: #555;
  font-size: 10px;
  margin-bottom: 2px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.day-cell {
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #000;
  cursor: pointer;
  border: 1px solid transparent;
}

.day-cell:hover:not(.disabled) {
  background: rgba(255,255,255,0.4);
  border-color: #000;
}

.day-cell.other-month {
  color: #888;
  opacity: 0.55;
}

.day-cell.selected {
  background: var(--mc-green, #5ea818);
  color: #fff;
  border-color: #000;
}

.day-cell.today {
  color: #2f7d1b;
  font-weight: bold;
}

.day-cell.disabled {
  color: #aaa;
  cursor: not-allowed;
  background: #b4b4b4;
}

.calendar-footer {
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
  gap: 6px;
}

.mc-mini-btn {
  flex: 1;
  padding: 4px;
  border: 2px solid #000;
  font-family: inherit;
  font-size: 10px;
  background: #999;
  box-shadow: inset -2px -2px 0 #555, inset 2px 2px 0 #ddd;
  cursor: pointer;
}

.mc-mini-btn:hover {
  filter: brightness(1.1);
}

.mc-fade-enter-active, .mc-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.mc-fade-enter-from, .mc-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
